import { LightObject, LightModel, BookingDB } from "./BookingLightDB"
import { computed, observable, action } from "mobx"
import dayjs, { Dayjs } from "dayjs"
import { SessionType, $SessionType } from "./SessionType"
import { Member } from "./Member"
import { SessionGroup, $SessionGroup } from "./SessionGroup"

export interface $Session extends LightObject {
  $id: string
  dateString: string
  $sessionType: string
  $sessionGroups: { [$id: string]: string }
  capacity?: number
}

export class Session implements LightModel {
  constructor(db: BookingDB, object: $Session) {
    this.db = db
    this.object = object
  }

  @computed
  get id() {
    return this.object.$id
  }

  static timeSlotFormat = "h:mma"

  @computed
  get timeSlot(): string {
    return this.date.format(Session.timeSlotFormat)
  }

  @computed
  get endDate(): Dayjs {
    return this.date.add(this.sessionType.object.durationInMinutes, "minute")
  }

  @computed
  get endTimeString(): string {
    return this.endDate.format("h:mma")
  }

  @computed
  get type() {
    return this.object.$type
  }

  isValid = true

  invalidMessage: string

  clean() {}

  @computed
  get lookupValues() {
    return [this.timeSlotIndex]
  }

  @computed
  get children() {
    return this.sessionGroups
  }

  @computed
  get date(): Dayjs {
    return dayjs(this.object.dateString)
  }

  @computed
  get description() {
    return this.sessionType.object.name
  }

  @computed
  get timeSlotIndex() {
    const x = `${this.sessionType.object.name}:${this.date.format(
      "MMM:DD:YYYY:h:mma"
    )}`
    return x
  }

  @computed
  get sessionType(): SessionType {
    return new SessionType(this.db, this.db.file.objects[
      this.object.$sessionType
    ] as $SessionType)
  }

  db: BookingDB

  @observable
  object: $Session

  @computed
  get sessionGroups(): SessionGroup[] {
    if (this.object.$sessionGroups) {
      return Object.keys(this.object.$sessionGroups).map(k => {
        const object = this.db.file.objects[k]
        return new SessionGroup(this.db, object as $SessionGroup)
      })
    }
    return []
  }

  @computed
  get members(): Member[] {
    const arr: Member[] = []
    this.sessionGroups.forEach(group => {
      group.members.forEach(m => {
        arr.push(m)
      })
    })
    return arr
  }

  @computed
  get isInFuture() {
    return this.date.isAfter(dayjs().subtract(15, "minute"))
  }

  @computed
  get capacityLeft(): number {
    const left = this.capacity - this.members.length
    return Math.max(left, 0)
  }

  @computed
  get capacity(): number {
    return this.object.capacity != undefined
      ? this.object.capacity
      : this.sessionType.object.capacity
  }

  @computed
  get numberBooked() {
    return this.capacity - this.capacityLeft
  }

  @action.bound
  addSessionGroup(sessionGroup: SessionGroup) {
    this.object.$sessionGroups[sessionGroup.object.$id] =
      sessionGroup.object.$id
  }

  @action.bound
  removeSessionGroup(sessionGroup: SessionGroup) {
    delete this.object.$sessionGroups[sessionGroup.object.$id]
  }
}
